import { useEffect } from "react";
import {
  useWebSocketStore,
  SubscriptionConfig,
  WSEvent,
  getSubscriptionKey,
} from "../store/websocketStore";

/**
 * Hook to subscribe to WebSocket events for a specific entity.
 * Connection is managed globally - this hook only handles subscriptions.
 *
 * @example
 * // Subscribe to a single project
 * useWsSubscription(
 *   { type: "project", id: projectId },
 *   useCallback((event) => {
 *     console.log("Project updated:", event);
 *   }, [])
 * );
 *
 * @example
 * // Subscribe to multiple projects
 * useWsSubscription(
 *   projects.map(p => ({ type: "project" as const, id: p.id })),
 *   useCallback((event) => {
 *     console.log("Project updated:", event);
 *   }, [])
 * );
 */
export function useWsSubscription(
  configs: SubscriptionConfig | SubscriptionConfig[],
  handler: (event: WSEvent) => void
): { isConnected: boolean } {
  const subscribe = useWebSocketStore((state) => state.subscribe);
  const unsubscribe = useWebSocketStore((state) => state.unsubscribe);
  const isConnected = useWebSocketStore((state) => state.isConnected);

  const configArray = Array.isArray(configs) ? configs : [configs];

  // Filter out invalid configs (empty ids)
  const validConfigs = configArray.filter((config) => config.id);

  // Stable key for dependency to not use an array that is changing
  const configsKey = validConfigs
    .map(getSubscriptionKey)
    .sort((a, b) => a.localeCompare(b))
    .join(",");

  useEffect(() => {
    const subscriptions = useWebSocketStore.getState().subscriptions;
    const newKeys = new Set(validConfigs.map(getSubscriptionKey));

    // Unsubscribe from removed: in store with our handler but not in new config
    subscriptions.forEach((sub, key) => {
      if (sub.handlers.has(handler) && !newKeys.has(key)) {
        unsubscribe(sub.config, handler);
      }
    });

    validConfigs.forEach((config) => {
      subscribe(config, handler);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configsKey, handler, subscribe, unsubscribe]);

  // Unmount cleanup - unsubscribe from everything this handler is on
  useEffect(() => {
    return () => {
      const subscriptions = useWebSocketStore.getState().subscriptions;
      subscriptions.forEach((sub) => {
        if (sub.handlers.has(handler)) {
          unsubscribe(sub.config, handler);
        }
      });
    };
  }, [handler, unsubscribe]);

  return { isConnected };
}
